import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { TextChipInput, type TextChipInputHandle } from "@/components/ui/TextChipInput";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { useHubToast } from "@/components/ui/HubToast";
import { ApiError } from "@/lib/api";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";

import { QualityApprovalPicker } from "./QualityApprovalPicker";
import { QualityCustomerPicker } from "./QualityCustomerPicker";
import { QualityFreshdeskTicketPicker } from "./QualityFreshdeskTicketPicker";
import {
  EMPTY_RMA_ADDRESS,
  QualityRmaAddressFields,
  rmaAddressCountry,
  type QualityRmaAddressValue,
} from "./QualityRmaAddressFields";
import { QualityRmaSavedAddressPicker } from "./QualityRmaSavedAddressPicker";
import { QualityRmaQuantityLines } from "./QualityRmaQuantityLines";
import { QualitySalesOrderPicker } from "./QualitySalesOrderPicker";
import { createQualityRma, rmaLedByLabel, type CreateQualityRmaInput, type RmaLedBy } from "./rmaApi";
import { fetchQualityEvent } from "./qualityEventPageApi";
import { formatRmaQuantityModelLines, type RmaQuantityModelLine } from "./rmaQuantity";

const HELP_TEXT = [
  "Creates an RMA number in the RMA-YYYY-MM-NN format for the current Chicago month.",
  "Replacement sales order, approval, and both addresses can wait until after the RMA is open.",
].join(" ");

export function QualityRmaNewPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const qualityEventIdRaw = Number(searchParams.get("qualityEventId"));
  const qualityEventId = Number.isFinite(qualityEventIdRaw) && qualityEventIdRaw > 0 ? qualityEventIdRaw : null;
  const { showToast } = useHubToast();
  const writesAllowed = useHubModuleWrite("quality");
  const [ledBy, setLedBy] = useState<RmaLedBy>("customer");
  const [complaint, setComplaint] = useState("");
  const [skuLines, setSkuLines] = useState<RmaQuantityModelLine[]>([{ quantity: "1", models: "", reason: "" }]);
  const [purchaseReference, setPurchaseReference] = useState("");
  const [customerFsAccountId, setCustomerFsAccountId] = useState<number | null>(null);
  const [endUserFsAccountId, setEndUserFsAccountId] = useState<number | null>(null);
  const [salesOrderIds, setSalesOrderIds] = useState<number[]>([]);
  const [approvalTicketIds, setApprovalTicketIds] = useState<number[]>([]);
  const [freshdeskTicketIds, setFreshdeskTicketIds] = useState<number[]>([]);
  const [deviceLabels, setDeviceLabels] = useState<string[]>([]);
  const deviceChipRef = useRef<TextChipInputHandle>(null);
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [shipTo, setShipTo] = useState<QualityRmaAddressValue>(EMPTY_RMA_ADDRESS);
  const [shipFrom, setShipFrom] = useState<QualityRmaAddressValue>(EMPTY_RMA_ADDRESS);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const prefilledEventId = useRef<number | null>(null);

  const sourceEventQuery = useQuery({
    queryKey: ["quality", "event", qualityEventId, "rma-prefill"],
    queryFn: () => fetchQualityEvent(qualityEventId ?? 0),
    enabled: qualityEventId != null,
  });

  useEffect(() => {
    const source = sourceEventQuery.data;
    if (source == null || prefilledEventId.current === source.id) return;
    prefilledEventId.current = source.id;
    setComplaint(source.summary?.trim() ?? "");
    if (source.customerFsAccountId != null) setCustomerFsAccountId(source.customerFsAccountId);
    if (source.freshdeskTicketIds.length > 0) setFreshdeskTicketIds(source.freshdeskTicketIds);
  }, [sourceEventQuery.data]);

  const createMutation = useMutation({
    mutationFn: createQualityRma,
    onSuccess: (detail) => {
      showToast({
        tone: "success",
        message:
          detail.startedApprovalTicketId != null
            ? `Opened ${detail.rma.rmaNumber} and started a replacement approval.`
            : `Opened ${detail.rma.rmaNumber}.`,
      });
      void navigate(`/quality/rmas/${detail.rma.id}`);
    },
    onError: (error: unknown) => {
      setErrorMessage(error instanceof ApiError ? error.message : "Could not create the RMA.");
    },
  });

  const buildCreateInput = (startReplacementApproval: boolean): CreateQualityRmaInput => ({
    ledBy,
    complaint: complaint.trim() || null,
    quantityAndModels: formatRmaQuantityModelLines(skuLines),
    purchaseReference: purchaseReference.trim() || null,
    customerFsAccountId,
    endUserFsAccountId,
    salesOrderIds,
    approvalTicketIds,
    startReplacementApproval,
    freshdeskTicketIds,
    deviceLabels: deviceChipRef.current?.flushDraft() ?? deviceLabels,
    contactName: contactName.trim() || null,
    contactEmail: contactEmail.trim() || null,
    contactPhone: contactPhone.trim() || null,
    shipFromLine1: shipFrom.line1.trim() || null,
    shipFromLine2: shipFrom.line2.trim() || null,
    shipFromCity: shipFrom.city.trim() || null,
    shipFromState: shipFrom.state.trim() || null,
    shipFromPostalCode: shipFrom.postalCode.trim() || null,
    shipFromCountry: rmaAddressCountry(shipFrom),
    shipToLine1: shipTo.line1.trim() || null,
    shipToLine2: shipTo.line2.trim() || null,
    shipToCity: shipTo.city.trim() || null,
    shipToState: shipTo.state.trim() || null,
    shipToPostalCode: shipTo.postalCode.trim() || null,
    shipToCountry: rmaAddressCountry(shipTo),
    qualityEventId,
  });

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    if (!writesAllowed) return;
    setErrorMessage(null);
    createMutation.mutate(buildCreateInput(false));
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <header className="flex flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp hoverHint={HELP_TEXT} breadcrumbLabel="New RMA">
          New RMA
        </PageTitleWithHelp>
        <Button type="submit" disabled={!writesAllowed || createMutation.isPending}>
          {createMutation.isPending ? "Creating…" : "Create RMA"}
        </Button>
      </header>

      {errorMessage ? <p className="text-sm text-red-700">{errorMessage}</p> : null}

      <Card>
        <CardHeader>
          <CardTitle>Return request</CardTitle>
        </CardHeader>
        <CardBody className="grid gap-3 md:grid-cols-2">
          <div className="md:col-span-2">
            <Label>Who is leading this RMA</Label>
            <SegmentedControl
              aria-label="Who is leading this RMA"
              className="mt-1 h-10 w-full max-w-md"
              value={ledBy}
              onChange={setLedBy}
              options={[
                { value: "customer", label: rmaLedByLabel("customer") },
                { value: "end_user", label: rmaLedByLabel("end_user") },
              ]}
            />
          </div>
          <div>
            <Label>Customer (purchased from)</Label>
            <QualityCustomerPicker
              id="rma-customer"
              value={customerFsAccountId}
              onChange={setCustomerFsAccountId}
            />
          </div>
          <div>
            <Label>End user</Label>
            <QualityCustomerPicker
              id="rma-end-user"
              value={endUserFsAccountId}
              onChange={setEndUserFsAccountId}
            />
          </div>
          <QualityRmaQuantityLines idPrefix="rma" lines={skuLines} onChange={setSkuLines} />
          <div className="md:col-span-2">
            <Label htmlFor="rma-complaint">Complaint</Label>
            <Input
              id="rma-complaint"
              value={complaint}
              onChange={(event) => setComplaint(event.target.value)}
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="rma-purchase-ref">Purchase date / sales order / purchase order / invoice (optional)</Label>
            <Input
              id="rma-purchase-ref"
              value={purchaseReference}
              onChange={(event) => setPurchaseReference(event.target.value)}
              placeholder="GSO-201975"
            />
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Links</CardTitle>
        </CardHeader>
        <CardBody className="grid gap-3 md:grid-cols-2">
          <div className="md:col-span-2">
            <Label>Replacement sales order (optional)</Label>
            <QualitySalesOrderPicker
              id="rma-sales-order"
              values={salesOrderIds}
              onChange={setSalesOrderIds}
            />
          </div>
          <div className="md:col-span-2">
            <Label>Replacement approval (optional)</Label>
            <div className="flex flex-wrap items-end gap-2">
              <div className="min-w-[16rem] flex-1">
                <QualityApprovalPicker
                  id="rma-approval"
                  values={approvalTicketIds}
                  onChange={setApprovalTicketIds}
                  disabled={!writesAllowed || createMutation.isPending}
                />
              </div>
              <Button
                type="button"
                variant="secondary"
                disabled={!writesAllowed || createMutation.isPending}
                onClick={() => {
                  if (!writesAllowed) return;
                  setErrorMessage(null);
                  createMutation.mutate(buildCreateInput(true));
                }}
              >
                {createMutation.isPending ? "Creating…" : "Create RMA and start approval"}
              </Button>
            </div>
          </div>
          <div className="md:col-span-2">
            <Label>Freshdesk tickets (optional)</Label>
            <QualityFreshdeskTicketPicker
              id="rma-freshdesk"
              values={freshdeskTicketIds}
              onChange={setFreshdeskTicketIds}
              disabled={!writesAllowed || createMutation.isPending}
            />
          </div>
          <div className="md:col-span-2">
            <TextChipInput
              ref={deviceChipRef}
              id="rma-devices"
              label="Device labels (optional)"
              chips={deviceLabels}
              onChipsChange={setDeviceLabels}
              placeholder="Type a serial and press Enter"
              disabled={!writesAllowed || createMutation.isPending}
            />
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Replacement ship-to (optional)</CardTitle>
        </CardHeader>
        <CardBody className="grid gap-3 md:grid-cols-2">
          <p className="md:col-span-2 text-sm text-slate-600">
            Where replacements should be delivered. You can create the RMA now and add this address afterward.
          </p>
          <QualityRmaSavedAddressPicker
            id="rma-ship-to-saved"
            customerFsAccountId={customerFsAccountId}
            endUserFsAccountId={endUserFsAccountId}
            disabled={!writesAllowed || createMutation.isPending}
            onApply={setShipTo}
          />
          <QualityRmaAddressFields idPrefix="rma-ship-to" values={shipTo} onChange={setShipTo} />
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Return ship-from (optional)</CardTitle>
        </CardHeader>
        <CardBody className="grid gap-3 md:grid-cols-2">
          <p className="md:col-span-2 text-sm text-slate-600">
            Needed later to generate a UPS return label. You can create the RMA now and add this address afterward.
          </p>
          <QualityRmaSavedAddressPicker
            id="rma-ship-from-saved"
            customerFsAccountId={customerFsAccountId}
            endUserFsAccountId={endUserFsAccountId}
            disabled={!writesAllowed || createMutation.isPending}
            onApply={setShipFrom}
          />
          <div className="md:col-span-2">
            <Button
              type="button"
              variant="secondary"
              disabled={!writesAllowed || createMutation.isPending}
              onClick={() => setShipFrom({ ...shipTo })}
            >
              Copy from replacement ship-to
            </Button>
          </div>
          <div>
            <Label htmlFor="rma-contact-name">Contact name</Label>
            <Input id="rma-contact-name" value={contactName} onChange={(event) => setContactName(event.target.value)} />
          </div>
          <div>
            <Label htmlFor="rma-contact-email">Contact email</Label>
            <Input id="rma-contact-email" value={contactEmail} onChange={(event) => setContactEmail(event.target.value)} />
          </div>
          <div>
            <Label htmlFor="rma-contact-phone">Contact phone</Label>
            <Input id="rma-contact-phone" value={contactPhone} onChange={(event) => setContactPhone(event.target.value)} />
          </div>
          <QualityRmaAddressFields idPrefix="rma-ship-from" values={shipFrom} onChange={setShipFrom} />
        </CardBody>
      </Card>
    </form>
  );
}
